import { useTheme } from "@mui/material";
import { ModeGoodsIn20Px, useToast } from "@qubit/autoparts";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { useConfigurePortsWithRetry } from "~/hooks/useConfigurePortsWithRetry";
import { useFcNavigate } from "~/hooks/useFcNavigate";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";

import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { sendUserTaskSelectedEvent } from "~/redux/actions/userEvents";
import { selectUserCanDecant } from "~/redux/selectors/authSelectors";
import {
  selectThisWorkstation,
  selectWorkstationHasInductionRole
} from "~/redux/selectors/workstationsSelectors";
import { usePostWorkstationInductionConfigurationMutation } from "~/redux/warehouse/workstation.hooks";

import { TemperatureZone } from "~/types/api";

import { MainButton } from "./MainButton";
import { PreparingWorkstationModal } from "./PreparingWorkstationModal";
import { SelectTempZoneModal } from "./SelectTempZoneModal";

export type Props = {
  route: string;
};

export function InductionButton({ route }: Props) {
  const { t } = useTranslation();
  const navigate = useFcNavigate();
  const { palette } = useTheme();
  const { search }: { search: string } = useLocation();
  const dispatch = useAppDispatch();
  const { errorToast } = useToast();
  const trackedPageName = useMixpanelPageName();
  const { isMultiTempGrid } = useGetMultiTempGridInfo();

  const [isTempZoneModalOpen, setIsTempZoneModalOpen] = useState(false);

  const isInductionRoleFound = useAppSelector(
    selectWorkstationHasInductionRole
  );
  const disableDecanting = !useAppSelector(selectUserCanDecant);
  const siteWorkstation = useAppSelector(selectThisWorkstation);
  const hasWorkstation = !!siteWorkstation;

  const [
    postInductionConfiguration,
    { isLoading: isSavingInductionConfiguration }
  ] = usePostWorkstationInductionConfigurationMutation();

  const { configurePortsWithRetry, isConfigurePortsRetrying } =
    useConfigurePortsWithRetry({
      workstationMode: "Induction"
    });

  const navigateToInduction = () => {
    navigate({ pathname: route, search });
    mixpanelTrack({
      type: "Button Click",
      label:
        route === "/autostore-putaway"
          ? "Induction V1 Button"
          : "Induction V2 Button",
      trackedPageName
    });
  };

  const onInductionButtonClicked = async () => {
    if (!disableDecanting && (isInductionRoleFound || !hasWorkstation)) {
      const isConfigurePortsSuccess = await configurePortsWithRetry();
      if (!isConfigurePortsSuccess) {
        return;
      }

      await dispatch(sendUserTaskSelectedEvent("INDUCTION"));

      if (isMultiTempGrid) {
        setIsTempZoneModalOpen(true);
      } else {
        navigateToInduction();
      }
    }
  };

  const handleTempZoneSelected = async (selectedZone: TemperatureZone) => {
    if (!siteWorkstation) {
      errorToast(t("no workstation selected"));
      return;
    }

    try {
      await postInductionConfiguration({
        autostoreGridId: siteWorkstation.autostoreGridId,
        workstationId: siteWorkstation.id,
        body: { temperatureZone: selectedZone }
      }).unwrap();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
      return;
    }

    setIsTempZoneModalOpen(false);
    navigateToInduction();
  };

  const alert =
    (!hasWorkstation && t("no workstation selected")) ||
    (!isInductionRoleFound && hasWorkstation && t("no induction role found")) ||
    (disableDecanting && t("user has insufficient privileges")) ||
    undefined;

  const alertKind =
    (!hasWorkstation && "workstation") ||
    (!isInductionRoleFound && hasWorkstation && "workstation") ||
    (disableDecanting && "user") ||
    undefined;

  return (
    <>
      <MainButton
        alert={alert}
        alertKind={alertKind}
        onClick={onInductionButtonClicked}
        disabled={(!isInductionRoleFound && hasWorkstation) || disableDecanting}
        icon={
          <ModeGoodsIn20Px
            width={30}
            height={30}
            fill={palette.primary.contrastText}
          />
        }
      >
        {t("nav.link.induction")}
      </MainButton>
      <PreparingWorkstationModal isOpen={isConfigurePortsRetrying} />
      {isMultiTempGrid && (
        <SelectTempZoneModal
          open={isTempZoneModalOpen}
          onClose={() => setIsTempZoneModalOpen(false)}
          onStart={handleTempZoneSelected}
          isSubmitting={isSavingInductionConfiguration}
          variant="start"
        />
      )}
    </>
  );
}
